import React from "react";
import { useTranslation } from "react-i18next";
import LegalDocumentView, { LegalSection } from "./LegalDocumentView";

const UserTerms: React.FC = () => {
  const { t } = useTranslation();

  const sections: LegalSection[] = [
    {
      title: t("legal.userTerms.sections.general.title"),
      subsections: t("legal.userTerms.sections.general.subsections", {
        returnObjects: true,
      }) as Array<{
        number: string;
        text: string;
        bullets?: string[];
      }>,
    },
    {
      title: t("legal.userTerms.sections.responsibilities.title"),
      subsections: t("legal.userTerms.sections.responsibilities.subsections", {
        returnObjects: true,
      }) as Array<{
        number: string;
        text: string;
      }>,
    },
    {
      title: t("legal.userTerms.sections.serviceDelivery.title"),
      subsections: t("legal.userTerms.sections.serviceDelivery.subsections", {
        returnObjects: true,
      }) as Array<{
        number: string;
        text: string | Array<string | { text: string; strong?: boolean; linkUrl?: string }>;
      }>,
    },
    {
      title: t("legal.userTerms.sections.subscriptionAccount.title"),
      subsections: t("legal.userTerms.sections.subscriptionAccount.subsections", {
        returnObjects: true,
      }) as Array<{
        number: string;
        text: string | Array<string | { text: string; strong?: boolean; linkUrl?: string }>;
        subsections?: Array<{
          number: string;
          text: string;
        }>;
        tailParagraphs?: Array<string>;
      }>,
    },
    {
      title: t("legal.userTerms.sections.interactionsWithOtherUsers.title"),
      subsections: t(
        "legal.userTerms.sections.interactionsWithOtherUsers.subsections",
        {
          returnObjects: true,
        }
      ) as Array<{
        number: string;
        text: string;
      }>,
    },
    {
      title: t("legal.userTerms.sections.internationalUsers.title"),
      subsections: t("legal.userTerms.sections.internationalUsers.subsections", {
        returnObjects: true,
      }) as Array<{
        number: string;
        text: string;
      }>,
    },
    {
      title: t("legal.userTerms.sections.intellectualPropertyRights.title"),
      subsections: t(
        "legal.userTerms.sections.intellectualPropertyRights.subsections",
        {
          returnObjects: true,
        }
      ) as Array<{
        number: string;
        text: string | Array<string | { text: string; strong?: boolean; linkUrl?: string }>;
        subsections?: Array<{
          number: string;
          text: string;
        }>;
        tailParagraphs?: Array<string>;
      }>,
    },
    {
      title: t("legal.userTerms.sections.generalRestrictionsOnUse.title"),
      subsections: t("legal.userTerms.sections.generalRestrictionsOnUse.subsections", {
        returnObjects: true,
      }) as Array<{
        number: string;
        text: string | Array<string | { text: string; strong?: boolean; linkUrl?: string }>;
        bullets?: string[];
        subsections?: Array<{
          number: string;
          text: string | Array<string | { text: string; strong?: boolean; linkUrl?: string }>;
          bullets?: string[];
          subsections?: Array<{
            number: string;
            text: string;
          }>;
        }>;
      }>,
    },
    {
      title: t("legal.userTerms.sections.externalMediaPlatforms.title"),
      subsections: t("legal.userTerms.sections.externalMediaPlatforms.subsections", {
        returnObjects: true,
      }) as Array<{
        number: string;
        text: string | Array<string | { text: string; strong?: boolean; linkUrl?: string }>;
        subsections?: Array<{
          number: string;
          text: string | Array<string | { text: string; strong?: boolean; linkUrl?: string }>;
        }>;
        tailParagraphs?: Array<string | Array<string | { text: string; strong?: boolean; linkUrl?: string }>>;
      }>,
    },
    {
      title: t("legal.userTerms.sections.processingOfPersonalData.title"),
      subsections: t("legal.userTerms.sections.processingOfPersonalData.subsections", {
        returnObjects: true,
      }) as Array<{
        number: string;
        text: string | Array<string | { text: string; strong?: boolean; linkUrl?: string }>;
        subsections?: Array<{
          number: string;
          text: string;
        }>;
      }>,
    },
    {
      title: t("legal.userTerms.sections.termsAndTermination.title"),
      subsections: t("legal.userTerms.sections.termsAndTermination.subsections", {
        returnObjects: true,
      }) as Array<{
        number: string;
        text: string | Array<string | { text: string; strong?: boolean; linkUrl?: string }>;
        subsections?: Array<{
          number: string;
          text: string;
        }>;
      }>,
    },
    {
      title: t("legal.userTerms.sections.limitationOfLiability.title"),
      subsections: t("legal.userTerms.sections.limitationOfLiability.subsections", {
        returnObjects: true,
      }) as Array<{
        number: string;
        text: string | Array<string | { text: string; strong?: boolean; linkUrl?: string }>;
        subsections?: Array<{
          number: string;
          text: string;
        }>;
      }>,
    },
    {
      title: t("legal.userTerms.sections.indemnification.title"),
      subsections: t("legal.userTerms.sections.indemnification.subsections", {
        returnObjects: true,
      }) as Array<{
        number: string;
        text: string | Array<string | { text: string; strong?: boolean; linkUrl?: string }>;
      }>,
    },
    {
      title: t("legal.userTerms.sections.forceMajeure.title"),
      subsections: t("legal.userTerms.sections.forceMajeure.subsections", {
        returnObjects: true,
      }) as Array<{
        number: string;
        text: string | Array<string | { text: string; strong?: boolean; linkUrl?: string }>;
      }>,
    },
    {
      title: t("legal.userTerms.sections.thirdPartyWebsites.title"),
      subsections: t("legal.userTerms.sections.thirdPartyWebsites.subsections", {
        returnObjects: true,
      }) as Array<{
        number: string;
        text: string | Array<string | { text: string; strong?: boolean; linkUrl?: string }>;
      }>,
    },
    {
      title: t("legal.userTerms.sections.changes.title"),
      subsections: t("legal.userTerms.sections.changes.subsections", {
        returnObjects: true,
      }) as Array<{
        number: string;
        text: string | Array<string | { text: string; strong?: boolean; linkUrl?: string }>;
      }>,
    },
    {
      title: t("legal.userTerms.sections.contact.title"),
      subsections: t("legal.userTerms.sections.contact.subsections", {
        returnObjects: true,
      }) as Array<{
        number: string;
        text: string | Array<string | { text: string; strong?: boolean; linkUrl?: string }>;
      }>,
    },
    {
      title: t("legal.userTerms.sections.otherProvisions.title"),
      subsections: t("legal.userTerms.sections.otherProvisions.subsections", {
        returnObjects: true,
      }) as Array<{
        number: string;
        text: string | Array<string | { text: string; strong?: boolean; linkUrl?: string }>;
      }>,
    },
    {
      title: t("legal.userTerms.sections.governingLaw.title"),
      subsections: t("legal.userTerms.sections.governingLaw.subsections", {
        returnObjects: true,
      }) as Array<{
        number: string;
        text: string | Array<string | { text: string; strong?: boolean; linkUrl?: string }>;
      }>,
    },
  ];

  return (
    <div className="space-y-6">
      <header className="border-b border-slate-200 pb-6">
        <h2 className="text-3xl font-bold tracking-tight text-slate-950 md:text-4xl">
          {t("legal.userTerms.title")}
        </h2>
        <p className="mt-3 text-base font-medium text-blue-700">
          {t("legal.userTerms.subtitle")}
        </p>
        <p className="mt-4 max-w-4xl text-base leading-8 text-slate-700 md:text-lg">
          {t("legal.userTerms.description")}
        </p>
      </header>

      <LegalDocumentView sections={sections} />
    </div>
  );
};

export default UserTerms;
